import { useState, useEffect } from 'react';
import classNames from 'classnames';
import { PorangeButton } from "@/components/atoms/PorangeButton.jsx";

export const Statement = ({ onAnswer }) => {
    const [showFirstLine, setShowFirstLine] = useState(false);
    const [showSecondLine, setShowSecondLine] = useState(false);
    const [showAnimation, setShowAnimation] = useState(false);
    const [showButton, setShowButton] = useState(false);
    const [isExiting, setIsExiting] = useState(false);
    const [animationExiting, setAnimationExiting] = useState(false);
    const [currentFoodIndex, setCurrentFoodIndex] = useState(0);

    const foods = [
        { emoji: "🍕", safe: "caution" },
        { emoji: "🌮", safe: "safe" },
        { emoji: "🥐", safe: "unsafe" },
        { emoji: "🥗", safe: "safe" },
        { emoji: "🍔", safe: "caution" },
        { emoji: "🥘", safe: "safe" },
        { emoji: "🍝", safe: "caution" },
        { emoji: "🍗", safe: "safe" },
        { emoji: "🍜", safe: "safe" },
        { emoji: "🥯", safe: "caution" },
        { emoji: "🍳", safe: "safe" },
        { emoji: "🍣", safe: "caution" },
        { emoji: "🥨", safe: "unsafe" },
        { emoji: "🍎", safe: "safe" },
    ];

    const getSafetyEmoji = (safety) => {
        switch(safety) {
            case "safe": return "✅";
            case "caution": return "⚠️";
            case "unsafe": return "❌";
            default: return "❌";
        }
    };

    useEffect(() => {
        setTimeout(() => setShowFirstLine(true), 100);
        setTimeout(() => setShowSecondLine(true), 400);
        setTimeout(() => setShowAnimation(true), 1000);
        setTimeout(() => setShowButton(true), 1600);

        // Cycle through foods every 2 seconds
        const interval = setInterval(() => {
            setCurrentFoodIndex(prev => (prev + 1) % foods.length);
        }, 2000);

        return () => clearInterval(interval);
    }, []);

    // Add this useEffect to log the current food for debugging
    useEffect(() => {
        console.log('Current food:', foods[currentFoodIndex]);
    }, [currentFoodIndex]);

    const handleContinue = () => {
        setAnimationExiting(true);
        setShowButton(false);
        setTimeout(() => {
            setIsExiting(true);
            setTimeout(() => onAnswer('continue'), 800);
        }, 400);
    };

    return (
        <div className={classNames(
            "relative h-full flex flex-col items-center justify-start px-6",
            isExiting ? "opacity-0" : "opacity-100",
            "transition-opacity duration-500"
        )}>
            <div className="text-center space-y-4 max-w-[600px] mt-[48px]">
                <div className="overflow-hidden">
                    <h2 
                        className={classNames(
                            "text-[32px] md:text-[40px] leading-tight font-bold transition-opacity duration-300 text-black",
                            showFirstLine ? "opacity-100" : "opacity-0"
                        )}
                    >
                       Atly works with {' '}
                        <span className="animate-shimmer bg-[length:200%_100%] bg-clip-text text-transparent bg-gradient-to-r from-pink-500 via-orange-500 to-pink-500">
                            doctors and dietitians
                        </span>{' '} to determine safety
                    </h2>
                </div>
                
                <div className="overflow-hidden">
                    <h3 
                        className={classNames(
                            "text-xl md:text-2xl text-gray-600 transition-opacity duration-300",
                            showSecondLine ? "opacity-100" : "opacity-0"
                        )}
                    >
                        We check kitchen protocols, menu items, staff training, and more
                    </h3>
                </div>
            </div>

            {showAnimation && (
                <div 
                    className={classNames(
                        "relative mt-24 transition-all duration-1000",
                        animationExiting ? "opacity-0" : "opacity-100",
                        showAnimation ? "translate-y-0" : "translate-y-8"
                    )}
                >
                    <div className="flex items-center justify-center space-x-12">
                        <div className="flex items-center justify-center">
                            <span className="text-7xl">👩‍⚕️</span>
                        </div>

                        <div className="relative flex items-center justify-center">
                            <div 
                                className="relative"
                                key={currentFoodIndex}
                            >
                                <span className="text-6xl animate-food-appear">
                                    {foods[currentFoodIndex].emoji}
                                </span>
                                <div 
                                    className={classNames(
                                        "absolute -right-8 -top-8 animate-result-appear text-4xl",
                                        {
                                            "text-green-500": foods[currentFoodIndex].safe === "safe",
                                            "text-orange-500": foods[currentFoodIndex].safe === "caution",
                                            "text-red-500": foods[currentFoodIndex].safe === "unsafe"
                                        }
                                    )}
                                >
                                    {getSafetyEmoji(foods[currentFoodIndex].safe)}
                                </div>
                            </div>

                            <div 
                                className="absolute w-full h-1 bg-gradient-to-r from-transparent via-blue-500 to-transparent animate-scan"
                                style={{ top: '50%' }}
                            />
                        </div>
                    </div>
                </div>
            )}

            <div 
                className={classNames(
                    "fixed bottom-0 left-0 right-0 p-5 transition-all duration-500 bg-gradient-to-t from-white via-white to-transparent pb-6 pt-10",
                    showButton ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8 pointer-events-none"
                )}
            >
                <PorangeButton
                    full={true}
                    className="h-[56px] rounded-[16px] text-lg shadow-md"
                    onClick={handleContinue}
                >
                    Continue
                </PorangeButton>
            </div>

            <style jsx global>{`
                @keyframes shimmer {
                    0% { background-position: 200% 0; }
                    100% { background-position: -200% 0; }
                }

                @keyframes scan {
                    0% { transform: translateY(-50px); opacity: 0; }
                    20% { opacity: 1; }
                    80% { opacity: 1; }
                    100% { transform: translateY(50px); opacity: 0; }
                }

                @keyframes food-appear {
                    0% { transform: scale(0); opacity: 0; }
                    50% { transform: scale(1.2); opacity: 0.8; }
                    100% { transform: scale(1); opacity: 1; }
                }

                @keyframes result-appear {
                    0% { transform: scale(0) rotate(-180deg); opacity: 0; }
                    100% { transform: scale(1) rotate(0deg); opacity: 1; }
                }

                .animate-shimmer {
                    animation: shimmer 3s linear infinite;
                }

                .animate-scan {
                    animation: scan 2s ease-in-out infinite;
                }

                .animate-food-appear {
                    animation: food-appear 0.8s ease-out;
                    animation-fill-mode: both;
                }

                .animate-result-appear {
                    animation: result-appear 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275);
                    animation-fill-mode: both;
                }
            `}</style>
        </div>
    );
};

export default Statement; 